import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

/**
 * Billing isn't wired up yet, so the in-app upgrade sheet records intent
 * instead of charging: which plan, which feature prompted it. Admin flips the
 * account to Pro by hand until Stripe / RevenueCat lands.
 */
export const requestUpgrade = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        plan: z.enum(["pro", "team"]).default("pro"),
        source: z.string().max(60).optional(),
        note: z.string().trim().max(500).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;

    // One open request per plan is plenty — don't spam the admin list.
    const { data: existing } = await supabase
      .from("upgrade_requests")
      .select("id, created_at")
      .eq("user_id", userId)
      .eq("plan", data.plan)
      .eq("status", "pending")
      .maybeSingle();
    if (existing) return { ok: true, alreadyRequested: true };

    const { error } = await supabase.from("upgrade_requests").insert({
      user_id: userId,
      plan: data.plan,
      source: data.source ?? null,
      note: data.note ?? null,
    });
    if (error) throw new Error(error.message);
    return { ok: true, alreadyRequested: false };
  });

/** Has this account already asked? Used to show "You're on the list". */
export const myUpgradeRequest = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("upgrade_requests")
      .select("id, plan, status, created_at")
      .eq("user_id", context.userId)
      .eq("status", "pending")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    return { request: data ?? null };
  });
